import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBranch } from "@/context/BranchContext";
import { useNotifications } from "@/context/NotificationsContext";
import { roleMeta } from "@/config/roles";

const TITLES: Record<string, string> = {
  "/dashboard": "الرئيسية",
  "/orders": "الطلبات",
  "/pos": "شاشة الكاشير",
  "/menu": "المنيو",
  "/settings": "إعدادات الفرع",
  "/reports": "التقارير",
  "/notifications": "الإشعارات",
  "/account": "حسابي",
};

export default function BranchHeader({ onMenuClick }: { onMenuClick: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { employee, logout } = useBranch();
  const { unreadCount } = useNotifications();
  const [open, setOpen] = useState(false);

  const role = roleMeta(employee?.role ?? "cashier");
  const title = TITLES[location.pathname] || "لوحة تحكم الفرع";

  const handleLogout = () => {
    setOpen(false);
    logout();
    navigate("/", { replace: true });
  };

  return (
    <header className="h-14 md:h-16 shrink-0 bg-background-50 border-b border-background-200 flex items-center justify-between px-3 md:px-6 sticky top-0 z-30">
      <div className="flex items-center gap-1.5 md:gap-2 min-w-0">
        <button
          type="button"
          onClick={onMenuClick}
          aria-label="فتح القائمة"
          className="lg:hidden w-9 h-9 md:w-10 md:h-10 rounded-btn flex items-center justify-center text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer shrink-0"
        >
          <i className="ri-menu-line text-xl"></i>
        </button>
        <h1 className="text-base md:text-lg font-bold text-foreground-900 truncate">{title}</h1>
      </div>

      <div className="flex items-center gap-1 md:gap-2 shrink-0">
        <button
          type="button"
          onClick={() => navigate("/notifications")}
          aria-label="الإشعارات"
          className="relative w-9 h-9 md:w-10 md:h-10 rounded-btn flex items-center justify-center text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
        >
          <i className="ri-notification-3-line text-xl"></i>
          {unreadCount > 0 ? (
            <span className="absolute top-1.5 right-1.5 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
              {unreadCount}
            </span>
          ) : null}
        </button>

        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-2 md:gap-2.5 h-10 md:h-11 pr-1 pl-1.5 md:pl-2 rounded-btn hover:bg-background-100 transition-colors cursor-pointer"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-sm md:text-base">
              {employee?.name?.charAt(0) || "م"}
            </div>
            <div className="text-right hidden sm:block leading-tight">
              <p className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{employee?.name}</p>
              <p className="text-xs text-foreground-400">{role.short}</p>
            </div>
            <i className="ri-arrow-down-s-line text-foreground-400"></i>
          </button>

          {open ? (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setOpen(false)}></div>
              <div className="absolute left-0 mt-2 w-56 max-w-[calc(100vw-1.5rem)] bg-background-50 rounded-lg shadow-dropdown border border-background-200 py-1.5 z-40 animate-fade-slide-down">
                <div className="px-4 py-2.5 border-b border-background-200">
                  <p className="text-sm font-semibold text-foreground-900">{employee?.name}</p>
                  <p className="text-xs text-foreground-400 mt-0.5" dir="ltr">{employee?.phone}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    navigate("/account");
                  }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-user-3-line text-lg"></i>
                  حسابي
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                >
                  <i className="ri-logout-box-r-line text-lg"></i>
                  تسجيل الخروج
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </header>
  );
}